import { getPaid, getRequested } from 'partner360/features/mdfActivities/helpers/sumActivities'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import type {
  CockpitPeriodKey,
  ICockpitKeyInitiative,
  ICockpitMdfActivity,
  ICockpitPlanningPeriod,
} from '../model/types'

import {
  filterCockpitMdfActivities,
  getCockpitInitiativesInScope,
  getInitiativeMdfPlannedAmount,
  getInitiativeMetricAmount,
  getInitiativePeriodAllocation,
  isSupportedInitiativeMetric,
} from './keyInitiativesTile'

type InitiativeValueType = 'Goal' | 'Incremental'

export type CockpitKeyInitiativesSummary = {
  initiativesCount: number
  financialGoal: number | null
  incrementalGoal: number | null
  mdfPlanned: number | null
  mdfRequested: number | null
  mdfPaid: number | null
}

type GetCockpitKeyInitiativesSummaryParams = {
  initiatives: ICockpitKeyInitiative[]
  mdfActivities: ICockpitMdfActivity[]
  fYear: number
  selectedOffering: string
  selectedCategory: string | null
  selectedPeriods: CockpitPeriodKey[]
  selectedMetric: ActualsValueTypes
  selectedCurrency: string
  planningPeriod: ICockpitPlanningPeriod | null
}

export const getCockpitKeyInitiativesSummary = ({
  initiatives,
  mdfActivities,
  fYear,
  selectedOffering,
  selectedCategory,
  selectedPeriods,
  selectedMetric,
  selectedCurrency,
  planningPeriod,
}: GetCockpitKeyInitiativesSummaryParams): CockpitKeyInitiativesSummary => {
  const isLocalCurrency = selectedCurrency !== 'USD'

  /** The same activities the expanded MDF sub-tab lists, so the two never disagree. */
  const filteredActivities = filterCockpitMdfActivities({
    activities: mdfActivities,
    selectedOffering,
    selectedPeriods,
    planningPeriod,
  })

  const filteredInitiatives = getCockpitInitiativesInScope({
    initiatives,
    fYear,
    selectedOffering,
    selectedCategory,
    selectedPeriods,
    planningPeriod,
  })

  const isSupportedMetric = isSupportedInitiativeMetric(selectedMetric)

  const sumFinancialValues = (valueType: InitiativeValueType) =>
    isSupportedMetric
      ? filteredInitiatives.reduce((sum, initiative) => {
          const amount = getInitiativeMetricAmount(
            initiative,
            valueType,
            selectedMetric,
            isLocalCurrency
          )
          const allocation = getInitiativePeriodAllocation(
            initiative.Period,
            selectedPeriods,
            planningPeriod?.fQuarter
          )

          return sum + (amount ?? 0) * allocation
        }, 0)
      : null

  return {
    initiativesCount: filteredInitiatives.length,
    financialGoal: sumFinancialValues('Goal'),
    incrementalGoal: sumFinancialValues('Incremental'),
    /** What the partner declared it would spend: the HP investment of the initiatives funded from
     *  MDF. This is a plan the initiative carries, not an activity amount — the activities' own
     *  requested/paid follow below. */
    mdfPlanned: filteredInitiatives.reduce(
      (sum, initiative) =>
        sum +
        getInitiativeMdfPlannedAmount(initiative, isLocalCurrency) *
          getInitiativePeriodAllocation(
            initiative.Period,
            selectedPeriods,
            planningPeriod?.fQuarter
          ),
      0
    ),
    mdfRequested: filteredActivities.reduce(
      (sum, activity) => sum + getRequested(activity, !isLocalCurrency),
      0
    ),
    mdfPaid: filteredActivities.reduce(
      (sum, activity) => sum + getPaid(activity, !isLocalCurrency),
      0
    ),
  }
}
